import { Type } from '@fullstory/proto/fs/api/vars';
import { ElementPropertyRuleRecord } from '@fullstory/proto/fs/services/search-settings';
import {
  CustomField,
  FieldScope,
  isIdentInSet,
  parseFieldName,
  partialFieldKey,
  unscopedCustomFieldIdent,
  VarType,
} from '@fullstory/search-vars';
import { PropertyRow } from './types';

export const buildPropertyRows = (
  fields: CustomField[],
  hidden: Set<string>,
  pending?: Set<string>,
): PropertyRow[] => {
  const propertyMap = new Map<string, PropertyRow>();
  for (const field of fields) {
    addFieldToPropertyMap(field, propertyMap);
  }

  // for properties rows, we keep archived connections as well
  // do a single pass to create a faster hidden connections lookup by prop
  const hiddenConnectionsByProperty = new Map<string, Set<string | number>>();
  hidden.forEach(h => {
    const firstColonIdx = h.indexOf(':');
    if (firstColonIdx > 0) {
      // need at least one character before the colon to map the property to a parent
      const prop = h.slice(0, firstColonIdx);
      const rawConnection = h.slice(firstColonIdx + 1, h.length);
      const connection =
        parseFieldName(prop).scope === FieldScope.Page
          ? parseInt(rawConnection, 10) // page connections have numeric parents
          : rawConnection; // while any other connections have string parents
      const hiddenConnections = hiddenConnectionsByProperty.get(prop);
      if (hiddenConnections) {
        hiddenConnections.add(connection);
      } else {
        hiddenConnectionsByProperty.set(prop, new Set<string | number>([connection]));
      }
    }
  });

  for (const [_, row] of propertyMap) {
    populatePropertyRow(
      row,
      hidden,
      hiddenConnectionsByProperty,
      false,
      new Set<string>(),
      pending,
    );
  }

  return [...propertyMap.values()];
};

/**
 * addFieldToPropertyMap adds a CustomField to a map of PropertyRows.
 *
 * @param field The CustomField from fieldmeta.
 * @param propertyMap The map of string to PropertyRows that will be updated with data from the field.
 */
const addFieldToPropertyMap = (field: CustomField, propertyMap: Map<string, PropertyRow>) => {
  const key = `${field.ident}_${field.varType}`;
  const curr = {
    id: key,
    displayName: field.ident,
    dataType: field.varType,
    propertyType: field.scope,
    archived: false,
    archivedConnections: new Set<string>(),
    selfArchived: false,
    selfArchivedConnections: new Set<string>(),
    pending: false,
  };
  propertyMap.set(key, curr);
};

/**
 * populatePropertyRowArchived fills in archived statuses on a PropertyRow and its children.
 *
 * A row has archived=true or a set archived connection if:
 *
 * - Its identifier is in the list of hidden vars.  In this case, the row will also have selfArchived=true.
 * - Any parent identifier is in the list of hidden vars.
 *
 *
 * NB: In the PropertiesTable, a row will be removed from the active view if all of its leaf children (rows
 * that have a value type rather than object type) are archived.  Those rows are still considered archived=false
 * in this selector.  They are filtered out as part of the table MenuFilter.
 *
 * @param row The PropertyRow whose own/children archived/selfArchived state will be populated.
 * @param hidden The set of hidden vars from fieldmeta.
 * @param hiddenConnectionsByProperty The set of hidden connections from fieldmeta, broken out by parent.
 * @param parentArchived Whether a parent row is considered archived.
 * @param parentArchivedConnections Whether a given set of parent connections is considered archived
 * @param pending The set of pending vars from other sources (i.e. property rules that have not yet been applied).
 */
const populatePropertyRow = (
  row: PropertyRow,
  hidden: Set<string>,
  hiddenConnectionsByProperty: Map<string, Set<string | number>>,
  parentArchived: boolean,
  parentArchivedConnections: Set<string | number>,
  pending?: Set<string>,
) => {
  row.selfArchived = isIdentInSet(hidden, row.propertyType, row.id);
  row.selfArchivedConnections =
    hiddenConnectionsByProperty.get(`${row.propertyType}_${row.id}`) ?? new Set<string>();
  row.archived = parentArchived || row.selfArchived;
  row.archivedConnections = new Set<string | number>(parentArchivedConnections);
  row.selfArchivedConnections.forEach(v => row.archivedConnections.add(v));
  row.pending = pending ? isIdentInSet(pending, row.propertyType, row.id) : false;
};

// forEachLeafProperty traverses children properties only calling the callback for those that are at the leaf.
// The prop passed along to the callback is typed (i.e. of the form '<name>_<typ>').
// The callback returns whether or not to continue.
export const forEachLeafProperty = (row: PropertyRow, callback: (prop: string) => boolean) => {
  const next: PropertyRow[] = [row];
  while (next.length > 0) {
    const cur = next.shift();
    if (cur) {
      // cur is a leaf, we can hit the callback using the id as the property
      if (!callback(cur.id)) {
        return;
      }
    }
  }
};

export const propertiesWithPending = (
  properties: CustomField[],
  propertyRules: ElementPropertyRuleRecord[],
) => {
  const mergedProperties = [...properties];
  const existing = new Set<string>();
  for (const field of properties) {
    existing.add(unscopedCustomFieldIdent(field));
  }
  const pending = new Set<string>();
  for (const rule of propertyRules) {
    if (rule.elementPropertyRule?.propertyName && rule.elementPropertyRule?.propertyType) {
      const field: CustomField = {
        scope: FieldScope.Element,
        ident: rule.elementPropertyRule.propertyName,
        varType: <VarType>Type[rule.elementPropertyRule.propertyType],
      };
      const fieldIdent = unscopedCustomFieldIdent(field);
      if (!existing.has(fieldIdent)) {
        mergedProperties.push(field);
        existing.add(fieldIdent);
        pending.add(partialFieldKey(FieldScope.Element, fieldIdent));
      }
    }
  }
  return { mergedProperties, pending };
};
